import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiGet, API_BASE } from '../utils/api';
import { AnimatedHeight } from '../hooks/useExpandAnimation';
import { DESKTOP_SECTION_CHEVRON, DESKTOP_SECTION_HEAD_BLEED } from './OrderSectionHeader';
import ActivityHistoryList from './ActivityHistoryList';

/**
 * THE order activity-history (audit-log) section. One definition, both surfaces.
 *
 * 🔑 Since 2026-10-29 the LIST itself is the shared `ActivityHistoryList`; this file
 * is its adapter and keeps what is order-specific: the fetch, the laziness, the memo,
 * the disclosure header and the action colour/label maps. Its rendered markup is
 * frozen byte for byte by `OrderActivityHistory.rowMarkup.test.js`.
 * Extracted from `pages/OrderPage.js` on 2026-08-10 when the expanded order row
 * on Department Orders / To Approve needed the same section. The action colour
 * and label maps had to move with it — two copies of those maps is how a new
 * audit action comes to render as a grey dot and a raw enum on one surface and
 * correctly on the other.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🚨 THE REQUEST FIRES ON DISCLOSURE-OPEN, NOT ON MOUNT AND NOT ON RENDER
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `GET /api/orders/:id/audit-log` is UNBOUNDED — one row per edit, per approval
 * step and per status change — so it is both the most expensive of the panel's
 * sections and the one least often wanted. Two consequences, and the second is
 * the load-bearing one:
 *
 *   1. It is COLLAPSED BY DEFAULT in the panel (see `defaultOpen` below), so a
 *      row does not triple in height the moment it is expanded.
 *   2. Because it is collapsed, fetching on mount would spend a request per
 *      expand on a section nobody looked at. It therefore fetches LAZILY, on
 *      the first open, and memoises — reopening the same order's history issues
 *      NOTHING.
 *
 * 🔑 Combined with `OrderTableRow` mounting `expandedContent` only inside
 * `{isExpanded && …}` and only once `detail` has resolved
 * (`components/OrderTableView.js:1611-1631`), this makes the request count a
 * STRUCTURAL property rather than a guarded one: a collapsed list issues zero
 * of these however many rows it renders, which is the client-side N+1 CLAUDE.md
 * names for this panel. Pinned by
 * `client/src/components/OrderExpandedDetails.requests.test.js`.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🟢 ACCOUNTING IS ALREADY REDACTED UPSTREAM — ADD NO GATE HERE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `server/helpers/orderEditDiff.js` records `cost_code_id`, `payment_type_id`
 * and `budget_pot_id` BY NAME ONLY, never with a value, because
 * `GET /api/orders/:id/audit-log` gates on `canViewOrder` while the accounting
 * fields are gated per order by `canViewOrderAccounting` — and a
 * `sub_department_lead` is seeded `can_view_department_orders = 1` /
 * `can_view_order_accounting = 0`, so THE AUDIT READER SET IS STRICTLY WIDER
 * THAN THE ACCOUNTING ONE. `details` is therefore safe to render to anyone the
 * endpoint answers. Nothing needs restoring and nothing needs hiding; do not
 * add a client-side accounting filter here, and never move a field out of that
 * helper's `ITEM_REDACTED_FIELDS`.
 */

// The audit-action vocabulary. ONE definition — see the docblock.
export const AUDIT_ACTION_COLORS = {
  submitted: 'bg-blue-500',
  approved: 'bg-green-500',
  rejected: 'bg-red-500',
  ordered: 'bg-indigo-500',
  delivered: 'bg-purple-500',
  edited: 'bg-yellow-500',
  resubmitted: 'bg-orange-500',
  cancelled: 'bg-gray-500',
  notes_updated: 'bg-gray-500',
};

export const AUDIT_ACTION_LABELS = {
  submitted: 'Submitted',
  approved: 'Approved',
  rejected: 'Rejected',
  ordered: 'Marked as Ordered',
  delivered: 'Marked as Delivered',
  edited: 'Edited',
  resubmitted: 'Resubmitted',
  cancelled: 'Cancelled',
  notes_updated: 'Notes Updated',
};

/**
 * 🔑 ONE localStorage KEY — `sfx_order_activity_history`, which
 * `pages/OrderPage.js` has always used and which, since 2026-09-12, ONLY the
 * full page reads or writes.
 *
 * 🚨 THE TWO SURFACES DIFFER ON *TWO* THINGS, AND BOTH ARE DELIBERATE. This
 * said "ONE THING ONLY" until 2026-09-12; the second was added that day.
 *
 *   1. THE DEFAULT WHEN THERE IS NO STORED PREFERENCE. The full page reads
 *      `!== 'collapsed'`, i.e. it opens EXPANDED; the expanded row passes
 *      `defaultOpen={false}` and opens COLLAPSED. A page devoted to one order
 *      has the height to spend and the history is a reason to be there; a table
 *      row does not, and an unbounded list would roughly triple the row on
 *      every expand.
 *   2. WHETHER THE USER'S CHOICE PERSISTS AT ALL. The expanded row also passes
 *      `persistOpenState={false}`, so it neither READS nor WRITES this key: the
 *      history is open only for as long as that one row stays expanded, and
 *      collapsing the row — or expanding any other — starts collapsed again.
 *
 * 🔻 WHY (2), the owner's ask on 2026-09-12: *"on expanded orders 'Activity
 * History' should always be collapsed"*. Before that the key was SHARED, and
 * `readActivityHistoryPreference` returns the STORED value whenever one is
 * present — so `defaultOpen={false}` only ever reached a user who had never
 * touched the section ANYWHERE. The moment anyone expanded the history on
 * either surface, `'expanded'` was written and from then on EVERY expanded row
 * opened with the unbounded list open: exactly the height regression the row's
 * default exists to prevent, caused by a preference the user expressed about a
 * different surface. A default alone cannot fix that; not persisting the row's
 * choice is what does.
 *
 * 🔴 THE FULL PAGE IS UNCHANGED — it still reads and writes the key, so a user
 * who collapses the history there still finds it collapsed there next time.
 * **Do not "fix" either difference by unifying it**: making the row default to
 * expanded is the height regression, making the page default to collapsed
 * changes shipped behaviour nobody complained about, and giving the row the key
 * back reinstates the defect the owner reported.
 */
export const ACTIVITY_HISTORY_KEY = 'sfx_order_activity_history';

/**
 * 🎨 TWO HEADER TREATMENTS — AND `card` IS BYTE-FOR-BYTE WHAT SHIPPED, so
 * `pages/OrderPage.js` (which passes nothing) renders exactly as it did.
 *
 * Product owner, 2026-08-10, looking at the expanded order row: *"I think active
 * history should look like 'Attachments / Delivery Paperwork' these sections
 * when collapsed it cutrently has its own loo comparared with the chevron arrow
 * of the other two sections above"*.
 *
 * 🔑 THE PANEL VARIANT IS NOT A NEW DESIGN. It is `components/OrderAttachments.js`'s
 * disclosure header copied — same flex/gap, same chevron size, same
 * `data-chevron`, same absence of chrome — because on that surface this section
 * sits directly beneath TWO renders of that very component and the reference is
 * whatever they already do. If `OrderAttachments`'s header ever changes, this
 * variant follows it; it has no independent opinion.
 *
 * 🚨 WHY THE CARD CHROME COULD NOT SIMPLY BE PASSED THROUGH `className`: every
 * one of these differences is hard-coded in the markup below, not in the two
 * class props, so no combination of `className` / `headingClassName` could reach
 * them. What the panel was getting, and why each is wrong THERE specifically:
 *
 *   • `-mx-2.5 -mt-2.5` — these cancel the CARD's own `p-2.5` on OrderPage, so
 *     the header sits flush to the card edge. The panel's container has NO
 *     padding at all (`border-t … pt-2`), so the same classes pulled the header
 *     10px OUTSIDE it on both sides and 10px up, overlapping its own `border-t`.
 *     A header that overhangs its two neighbours is most of "its own look".
 *   • `data-section-title` — `index.css:202-208` gives it
 *     `background-color: rgba(51,65,85,0.3) !important` in DARK MODE. So the
 *     history header was a filled bar between two unfilled ones, and only for
 *     dark-mode users. Nothing else reads the attribute on this element (the
 *     `<tr>` in `e2e/online-budget-ui.spec.js` is a different surface).
 *   • `border-b` + `rounded-t-lg` + `hover:bg-gray-50` — a card header's
 *     furniture. Beside two headers that have none, it reads as a different
 *     class of control.
 *   • The chevron sat AFTER the title (`justify-between`) at `h-4 w-4` with no
 *     `data-chevron`; the reference puts it BEFORE at `h-3.5 w-3.5` with it.
 *
 * ⚠️ `data-chevron` IS BEHAVIOUR, NOT DECORATION. `index.css:160-174` tints
 * every `[data-chevron]:not([data-actionable])` blue on ANY ancestor's hover
 * (light `#2563eb`, dark `#60a5fa`) — `:hover` matches every ancestor, which is
 * the documented CLAUDE.md hazard. Without it this chevron stayed grey while the
 * two above it went blue under the same pointer, so the sections differed when
 * touched as well as at rest.
 *
 * 🪤 THE CHEVRON'S `text-gray-400` IS BARE ON PURPOSE — DO NOT ADD A `dark:`
 * PAIR. `index.css:264-266` is `html.dark .text-gray-400 { color: #6b7280
 * !important }`, and an `!important` author declaration beats any non-important
 * rule whatever its specificity, so a `dark:text-*` here would compile and be
 * INERT — which is worse than none, because it reads as explicit dark support.
 * That rule is what delivers the chevron's dark value on all three sections, and
 * `components/OrderAttachments.js:150` is bare for the same reason.
 */
const HEADER_VARIANTS = {
  // The full order page: a full-bleed header on a `p-2.5 … rounded-lg shadow-md`
  // card. Unchanged since before this prop existed.
  card: {
    button: 'flex items-center justify-between w-full text-left -mx-2.5 -mt-2.5 px-2 py-1 border-b border-gray-200 dark:border-gray-700 rounded-t-lg hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors',
    chevronSize: 'h-4 w-4',
    chevronFirst: false,
    dataChevron: false,
    sectionTitle: true,
  },
  // The expanded order row: `components/OrderAttachments.js`'s header.
  inline: {
    // Its own card in the opened order, so the header bleeds + hovers (2026-10-10).
    button: `flex items-center gap-2 w-full text-left ${DESKTOP_SECTION_HEAD_BLEED}`,
    chevronSize: `h-3.5 w-3.5 ${DESKTOP_SECTION_CHEVRON}`, // desktop sections alike (2026-10-10)
    chevronFirst: true,
    dataChevron: true,
    sectionTitle: false,
  },
};

/**
 * 🔑 UNCHANGED, AND STILL EXPORTED — `pages/OrderPage.js`'s surface depends on
 * it through the default `persistOpenState={true}`. The non-persisting surface
 * does not "call it differently"; it BYPASSES it entirely, which is the whole
 * point — a stored value must not be able to reach that surface at all.
 */
export function readActivityHistoryPreference(defaultOpen) {
  try {
    const stored = localStorage.getItem(ACTIVITY_HISTORY_KEY);
    if (stored === 'expanded') return true;
    if (stored === 'collapsed') return false;
  } catch { /* noop */ }
  return defaultOpen;
}

function OrderActivityHistory({
  orderId,
  // The full page opens expanded, the expanded row collapsed — see the key's
  // docblock. With `persistOpenState` (the default) this is consulted only when
  // the user has never expressed a preference; without it, it is the ONLY thing
  // consulted and it decides every mount.
  defaultOpen = true,
  // 🚨 `false` makes this surface STATELESS across mounts: the stored
  // preference is neither read nor written, so `defaultOpen` decides every
  // mount and toggling here cannot change what any other surface shows. The
  // expanded order row passes it (owner, 2026-09-12) — see the key's docblock.
  // Default `true`, so `pages/OrderPage.js`, which passes nothing, is
  // byte-identical to what shipped.
  persistOpenState = true,
  // Bumped by a parent that has just written something the log should show
  // (OrderPage does this after a successful edit). A counter, not a boolean, so
  // consecutive saves each refetch. Ignored while the section has never opened.
  refreshSignal = 0,
  testIdPrefix = '',
  // `p-2.5 … shadow-md` on the full page; the panel passes its own lighter
  // chrome so the section sits inside an already-carded row.
  className = 'bg-white dark:bg-gray-800 rounded-lg shadow-md p-2.5 mt-4 space-y-4',
  headingClassName = 'text-lg font-semibold text-gray-700 dark:text-gray-200',
  // 'card' (default, the full order page) | 'inline' (the expanded order row,
  // where this must match the two OrderAttachments headers above it). See
  // HEADER_VARIANTS. An unrecognised value falls back to the shipped treatment.
  headerVariant = 'card',
}) {
  const variant = HEADER_VARIANTS[headerVariant] || HEADER_VARIANTS.card;
  const [open, setOpen] = useState(() => (
    persistOpenState ? readActivityHistoryPreference(defaultOpen) : defaultOpen
  ));
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  // Guards against a second in-flight request when a user double-clicks the
  // disclosure. NOT a stale-response guard — there is only ever one URL here
  // and the component remounts per order — see hooks/useSearchRequest.js for
  // the case that genuinely needs one.
  const inFlight = useRef(false);

  const tid = (suffix) => (testIdPrefix ? `${testIdPrefix}-${suffix}` : suffix);

  const fetchLog = useCallback(() => {
    if (!orderId || inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    apiGet(`${API_BASE}/api/orders/${orderId}/audit-log`)
      .then(res => (res.ok ? res.json() : []))
      .then(data => {
        setEntries(Array.isArray(data) ? data : []);
        setLoaded(true);
      })
      .catch(() => { /* a history that fails to load must not break the panel */ })
      .finally(() => { inFlight.current = false; setLoading(false); });
  }, [orderId]);

  // LAZY: the first open is what pays for the request. An unopened section
  // costs nothing, which is the whole reason this is not a mount-time fetch.
  useEffect(() => {
    if (open && !loaded) fetchLog();
  }, [open, loaded, fetchLog]);

  // A parent-signalled refresh only reloads a section that has ALREADY paid for
  // itself; seeding `refreshSignal` from its mount value would be the same
  // device `OrderInlineEditor`'s `dirtyWarnSignal` uses, but here the `loaded`
  // check already achieves it — an unopened section has nothing to refresh.
  const firstSignal = useRef(refreshSignal);
  useEffect(() => {
    if (refreshSignal === firstSignal.current) return;
    firstSignal.current = refreshSignal;
    if (loaded) fetchLog();
  }, [refreshSignal, loaded, fetchLog]);

  const toggle = (e) => {
    if (e) e.stopPropagation();
    setOpen(prev => {
      const next = !prev;
      // 🚨 BOTH HALVES OF `persistOpenState` ARE LOAD-BEARING AND NEITHER IS
      // SUFFICIENT. Skipping only the READ still lets a row's open write
      // `'expanded'` into the key the full page reads; skipping only the WRITE
      // still lets the full page's `'expanded'` open every row. The row must be
      // sealed in both directions — see the key's docblock.
      if (persistOpenState) {
        try { localStorage.setItem(ACTIVITY_HISTORY_KEY, next ? 'expanded' : 'collapsed'); } catch { /* noop */ }
      }
      return next;
    });
  };

  // ONE chevron, positioned by the variant. Rotation, glyph, stroke and the bare
  // `text-gray-400` are identical in both and identical to
  // `components/OrderAttachments.js:150` — only the size and `data-chevron`
  // differ, and both are the reference's values in the `inline` variant.
  const chevron = (
    <svg
      {...(variant.dataChevron ? { 'data-chevron': true } : {})}
      xmlns="http://www.w3.org/2000/svg"
      className={`${variant.chevronSize} text-gray-400 transition-transform ${open ? '' : '-rotate-90'}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );

  // The endpoint returns ORDER BY created_at ASC; the shared list renders its
  // entries AS GIVEN, so newest-first is made HERE. `[...entries]` because
  // `.reverse()` mutates in place. `impersonated_by` is deliberately NOT mapped to
  // `onBehalfOf`: the order history has never shown the impersonating admin
  // ("site admin should not show up on audits for users to see"), and it must not start.
  const listEntries = [...entries].reverse().map((entry) => ({
    id: entry.id,
    at: entry.created_at,
    actor: entry.performed_by,
    tag: AUDIT_ACTION_LABELS[entry.action] || entry.action,
    dotClass: AUDIT_ACTION_COLORS[entry.action] || 'bg-gray-400',
    details: entry.details,
  }));

  return (
    <div className={className} data-testid={tid('audit-trail-section')}>
      <button
        type="button"
        onClick={toggle}
        className={variant.button}
        aria-expanded={open}
        data-testid={tid('audit-trail-toggle')}
        data-phone-section-head=""
        {...(variant.sectionTitle ? { 'data-section-title': true } : {})}
      >
        {variant.chevronFirst && chevron}
        {/* 🔑 A REAL `<h3>`, WHERE THE REFERENCE USES A `<span>` — deliberate,
            and the ONE place this does not copy `OrderAttachments`. The two are
            visually identical because the panel passes the reference's own
            `text-sm font-medium text-gray-600 dark:text-gray-400` as
            `headingClassName`; the difference is that a heading is exposed to
            assistive technology as one. Matching the span would DELETE that,
            which is a loss, not a match — and the fix asked for is a visual one.
            Promoting the reference's span instead would be a redesign of the
            reference. */}
        <h3 className={headingClassName}>Activity History</h3>
        {!variant.chevronFirst && chevron}
      </button>
      {/* Eases open AND closed (owner 2026-10-09) — `hooks/useExpandAnimation.js`;
          always rendered, `open` does the rest. */}
      {(
        <AnimatedHeight open={open} phaseKey={loading ? 'loading' : 'ready'} animateOnMount>
        {loading ? (
          <p className="text-sm text-gray-500">Loading activity history...</p>
        ) : (
          <ActivityHistoryList entries={listEntries} testIdPrefix={tid('audit')} />
        )}
        </AnimatedHeight>
      )}
    </div>
  );
}

export default OrderActivityHistory;
